import { localDateOf, localToEpoch, nextDate, weekdayOf, zonedParts } from '@salon/scheduling';
import type { Locale } from './i18n';

/** Calendar maths happens in the salon's time zone, whatever the device is set to. */
export const SALON_TZ = 'Europe/Amsterdam';

export const todayIn = (tz: string) => localDateOf(Date.now(), tz);
export const dateOf = (iso: string | number, tz: string) => localDateOf(typeof iso === 'number' ? iso : Date.parse(iso), tz);

/** Minutes since local midnight. */
export function minuteOfDay(iso: string | number, tz: string) {
  const p = zonedParts(typeof iso === 'number' ? iso : Date.parse(iso), tz);
  return p.hour * 60 + p.minute;
}

/** A local date + minute of day → ISO instant. */
export function instantAt(date: string, minute: number, tz: string) {
  return new Date(localToEpoch(date, hhmm(minute), tz)).toISOString();
}

/** Hour of the day at the salon (for greetings). */
export const zonedHour = (epoch: number, tz: string) => zonedParts(epoch, tz).hour;

export const hhmm = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

export function addDays(date: string, n: number) {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Monday of the week of `date`. */
export const startOfWeek = (date: string) => addDays(date, 1 - weekdayOf(date));
export const weekDates = (date: string) => Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(date), i));
export { nextDate };

const tag = (l: Locale) => (l === 'nl' ? 'nl-NL' : 'en-GB');
const noon = (date: string) => new Date(`${date}T12:00:00Z`);

export const formatTime = (iso: string | number, tz: string) =>
  new Intl.DateTimeFormat('nl-NL', { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** "do 9 okt" */
export const formatDateShort = (date: string, l: Locale) =>
  new Intl.DateTimeFormat(tag(l), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(noon(date));

/** "donderdag 9 oktober" */
export const formatDateLong = (date: string, l: Locale) =>
  new Intl.DateTimeFormat(tag(l), { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(noon(date));

/** "6 – 12 okt" */
export function formatWeek(date: string, l: Locale) {
  const days = weekDates(date);
  const f = new Intl.DateTimeFormat(tag(l), { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${f.format(noon(days[0]!))} – ${f.format(noon(days[6]!))}`;
}

/** "3 okt 10:12" for history lines. */
export const formatStamp = (iso: string, tz: string, l: Locale) =>
  new Intl.DateTimeFormat(tag(l), { timeZone: tz, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
